import Fuse from 'fuse.js';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { selectedPatientProfilesFilterAtom } from '../../../../../../data/session-list/atoms';
import { useSessionListHost } from '../../../../../session-list/session-list-host-context';

/** cmdk returns 1 for a match and 0 for a miss. */
const FILTER_RESULTS = { MATCH: 1, NO_MATCH: 0 } as const;

export const usePatientProfileFilter = ({
  enabled = false,
}: { enabled?: boolean } = {}) => {
  const host = useSessionListHost();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPatientProfilesFilter, setSelectedPatientProfilesFilter] = useAtom(
    selectedPatientProfilesFilterAtom
  );
  const { data: unsortedPatientProfiles } = host.patients.useQueryPatientProfiles({
    // OR semantics — three independent triggers:
    // `isOpen`:
    //   live only for the enabled:false parent instances (PatientProfileFilter,
    //   SessionListPatientProfileFilter), which wire their own isOpen to the
    //   dropdown trigger; the nested picker's own instance never sets it.
    // `selected.length`: fires when a prior selection exists (chip popover,
    //   mobile main-view summary) so names resolve without needing an open event.
    // `enabled`: force-enable for callers that control visibility externally
    //   (mobile picker passes !hidden); defaults to false — bare consumers stay lazy.
    enabled: enabled || isOpen || selectedPatientProfilesFilter.length > 0,
  });
  const [patientProfiles, setPatientProfiles] = useState(unsortedPatientProfiles);
  const hasSortedOnOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !hasSortedOnOpen.current) {
      const sortedProfiles = [...(unsortedPatientProfiles ?? [])].sort((a, b) => {
        const aIsSelected = selectedPatientProfilesFilter.includes(a.id ?? '');
        const bIsSelected = selectedPatientProfilesFilter.includes(b.id ?? '');

        if (aIsSelected && !bIsSelected) {
          return -1;
        }
        if (!aIsSelected && bIsSelected) {
          return 1;
        }
        return 0;
      });
      setPatientProfiles(sortedProfiles);
      hasSortedOnOpen.current = true;
    } else if (!isOpen) {
      hasSortedOnOpen.current = false;
      // Keep the list live when closed so a resolved fetch is immediately visible
      // on next open rather than showing a stale mount-time snapshot.
      setPatientProfiles(unsortedPatientProfiles);
    }
  }, [isOpen, unsortedPatientProfiles, selectedPatientProfilesFilter, hasSortedOnOpen]);

  const [searchValue, setSearchValue] = useState('');

  const fuse = useMemo(
    () =>
      new Fuse(patientProfiles ?? [], {
        keys: ['demographic_string'],
        threshold: 0.4,
      }),
    [patientProfiles]
  );

  const handleFilter = useCallback(
    (value: string, search: string) => {
      const result = fuse.search(search);
      if (result.find((r) => r.item.id === value)) {
        return FILTER_RESULTS.MATCH;
      }
      return FILTER_RESULTS.NO_MATCH;
    },
    [fuse]
  );

  const handleSelect = useCallback(
    (profileId: string) => {
      setSelectedPatientProfilesFilter((prev) =>
        prev.includes(profileId)
          ? prev.filter((id) => id !== profileId)
          : [...prev, profileId]
      );
    },
    [setSelectedPatientProfilesFilter]
  );

  // const numberOfSessionsForPatientProfile = useCallback(
  //   (index: number) => {
  //     const patientProfile = patientProfiles?.at(index);
  //     if (!patientProfile) {
  //       return 0;
  //     }

  //     const patientProfileSessions = sessionsData?.sessions?.filter(
  //       (session) => session.patient_profile?.id === patientProfile.id
  //     );

  //     return patientProfileSessions?.length ?? 0;
  //   },
  //   [patientProfiles, sessionsData?.sessions]
  // );

  return {
    patientProfiles,
    selectedPatientProfilesFilter,
    searchValue,
    setSearchValue,
    handleFilter,
    handleSelect,
    // numberOfSessionsForPatientProfile,
    isOpen,
    setIsOpen,
  };
};
